import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import Seo from './Seo'
import Breadcrumbs from './Breadcrumbs'
import Faq from './Faq'
import type { QA } from './Faq'
import { SITE } from '../lib/site'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'

export interface RelatedCalc {
  label: string
  href: string
  blurb: string
}

// Step shape for HowTo schema. Each entry maps to one item in the visible
// numbered list. `name` is the short heading ("Measure and calculate"),
// `text` is the description. Keep both concise — Google's HowTo rich card
// truncates aggressively.
export interface HowToStep {
  name: string
  text: string
}

interface Props {
  title: string        // SEO <title>
  h1: string
  description: string  // meta description + intro lead
  path: string         // e.g. /guides/how-to-paint-a-room/
  updated: string      // ISO date (yyyy-mm-dd)
  category: string     // short label, e.g. "Paint & Walls"
  faqs: QA[]
  related: RelatedCalc[]
  // Optional structured steps for Google's HowTo rich result. When provided,
  // a HowTo JSON-LD block is emitted alongside the Article schema — this is
  // what makes step-by-step guides eligible for the rich step cards that
  // appear directly in Google search results. Keep step copy short.
  howToSteps?: HowToStep[]
  children: ReactNode  // article body (plain semantic HTML)
}

function articleSchema(h1: string, description: string, path: string, updated: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: h1,
    description,
    datePublished: updated,
    dateModified: updated,
    author: { '@type': 'Organization', name: SITE.name },
    publisher: { '@type': 'Organization', name: SITE.name },
    mainEntityOfPage: SITE.url + path,
  }
}

function howToSchema(h1: string, description: string, path: string, updated: string, steps: HowToStep[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: h1,
    description,
    datePublished: updated,
    dateModified: updated,
    mainEntityOfPage: SITE.url + path,
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  }
}

export default function GuideArticle({ title, h1, description, path, updated, category, faqs, related, howToSteps, children }: Props) {
  const locale = useLocale()
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)
  // Schema bundle: always emit Article; emit HowTo too when steps are provided.
  // Google + Perplexity treat HowTo as a step-card rich result, which is the
  // single biggest "free" ranking lever for step-by-step content.
  const schema: object[] = [articleSchema(h1, description, localizedHref(path, locale), updated)]
  if (howToSteps && howToSteps.length > 0) {
    schema.push(howToSchema(h1, description, localizedHref(path, locale), updated, howToSteps))
  }
  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <Seo title={title} description={description} path={path} schema={schema} />
      <Breadcrumbs items={[{ name: tr('crumb.home'), href: L('/') }, { name: tr('crumb.resources'), href: L('/resources/') }, { name: h1, href: L(path) }]} />

      <div className="text-xs font-semibold uppercase tracking-wide text-brand-text">{category}</div>
      <h1 className="mt-1 text-3xl md:text-4xl">{h1}</h1>
      <p className="mt-3 text-lg text-text-muted">{description}</p>
      <p className="mt-2 text-xs text-text-subtle">
        {tr('guide.updated')} {new Date(updated + 'T00:00:00').toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      {/* Tools callout (top) */}
      {related.length > 0 && (
        <div className="mt-6 rounded-xl border border-border bg-brand-tint/30 p-4">
          <div className="text-sm font-semibold text-text">{tr('guide.calculatorsForProject')}</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {related.map((r) => (
              <Link key={r.href} to={L(r.href)} className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-brand-text hover:border-brand">
                {r.label} →
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Article body — semantic HTML styled via arbitrary variants */}
      <article className="mt-8 text-text-body [&_a]:text-brand-text [&_a]:underline [&_h2]:mt-9 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-text [&_h3]:mt-6 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-text [&_li]:mt-1.5 [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-6 [&_p]:mt-4 [&_p]:leading-relaxed [&_strong]:text-text [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-6">
        {children}
      </article>

      {/* Related calculators (detailed, bottom) */}
      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-text">{tr('guide.getMaterialsList')}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {related.map((r) => (
              <Link
                key={r.href}
                to={L(r.href)}
                className="flex items-start gap-3 rounded-lg border border-border bg-surface p-4 transition-colors hover:border-brand hover:bg-brand-tint/20"
              >
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand text-xs text-white">→</span>
                <div>
                  <div className="font-medium text-text">{r.label}</div>
                  <div className="text-sm text-text-muted">{r.blurb}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Faq items={faqs} />

      {/* Planner CTA */}
      <section className="mt-12 rounded-2xl border border-border bg-surface p-7 text-center">
        <h2 className="text-xl font-semibold text-text">{tr('guide.plannerCtaH2')}</h2>
        <p className="mx-auto mt-2 max-w-xl text-text-muted">{tr('guide.plannerCtaBody')}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link to={L('/my-sheets/')} className="rounded-lg bg-primary px-6 py-3 font-semibold text-on-primary hover:opacity-90">{tr('guide.openPlanner')}</Link>
          <Link to={L('/resources/')} className="rounded-lg border-2 border-brand px-6 py-3 font-semibold text-brand-text hover:bg-brand-tint">{tr('guide.moreGuides')}</Link>
        </div>
      </section>

      <div className="mt-8 border-t border-border pt-4 text-xs text-text-subtle">{tr('guide.disclaimer')}</div>
    </div>
  )
}
